/**
 * Every string the site shows, in English.
 *
 * This file is the source of truth: `ml.ts` is a partial of it, and a key
 * missing there falls back to the English written here. Keys are grouped by
 * the screen they belong to and named for their area, so a string can be
 * traced back to its page without grepping the value.
 *
 * Not in here: prose the server sends (validation failures, notification
 * bodies, audit lines). It arrives already written, in English.
 */
import type { Phrase } from './types';
import { bookingEn } from './site/booking.en';
import { coreEn } from './site/core.en';
import { exploreEn } from './site/explore.en';
import { homeEn } from './site/home.en';
import { pagesEn } from './site/pages.en';
import { staffEn } from './site/staff.en';
import { venueEn } from './site/venue.en';

const base = {
  // ------------------------------------------------------------------ common
  appName: 'Aghosha',
  save: 'Save',
  saving: 'Saving…',
  cancel: 'Cancel',
  close: 'Close',
  delete: 'Delete',
  remove: 'Remove',
  edit: 'Edit',
  add: 'Add',
  back: 'Back',
  next: 'Next',
  previous: 'Previous',
  search: 'Search',
  clear: 'Clear',
  apply: 'Apply',
  retry: 'Try again',
  loading: 'Loading…',
  confirm: 'Confirm',
  yes: 'Yes',
  no: 'No',
  optional: 'optional',
  none: '—',
  today: 'Today',
  date: 'Date',
  time: 'Time',
  status: 'Status',
  actions: 'Actions',
  name: 'Name',
  email: 'Email',
  phone: 'Mobile number',
  password: 'Password',
  venue: 'Venue',
  venues: 'Venues',
  allVenues: 'All venues',
  guests: 'Guests',
  price: 'Price',
  total: 'Total',
  notSet: 'Not set',

  // ---------------------------------------------------------------- language
  language: 'Language',
  languageAutomatic: 'Automatic',
  languageAutomaticHint: 'Follow the browser',
  languageEnglish: 'English',
  languageMalayalam: 'മലയാളം',

  // --------------------------------------------------------------------- nav
  navMyBookings: 'My bookings',
  navAdmin: 'Admin',
  navNotifications: 'Notifications',
  navUnread: { one: '{count} unread', other: '{count} unread' },
  navOpenMenu: 'Open menu',
  navCloseMenu: 'Close menu',
  navLightMode: 'Light mode',
  navDarkMode: 'Dark mode',
  navToLight: 'Switch to light mode',
  navToDark: 'Switch to dark mode',

  // --------------------------------------------------------------- user menu
  menuAccount: 'Account: {name}',
  menuBookingCalendar: 'Booking calendar',
  menuManagerDashboard: 'Manager dashboard',
  menuFavorites: 'Favourites',
  menuEditDetails: 'Edit details',
  favLoginToSave: 'Log in to save venues to your favourites',
  favSaveFailed: 'Could not update your favourites. Please try again.',
  menuLogout: 'Log out',
  menuDeleteAccount: 'Delete account',
  menuLogoutConfirmTitle: 'Log out?',
  menuLogoutConfirmBody: 'You will need to log in again to see your bookings.',
  menuLogoutConfirmAction: 'Log out',

  // ------------------------------------------------------------------ footer
  footerBrowseVenues: 'Browse venues',
  footerMyBookings: 'My bookings',
  footerNotifications: 'Notifications',
  footerMyVenues: 'My venues',
  footerAdminVenues: 'Venues',
  footerCopyright: '© {year} Aghosha.',

  // --------------------------------------------------------------- not found
  notFoundTitle: 'Page not found',
  notFoundBody: 'This page does not exist.',

  // -------------------------------------------------------- booking calendar
  calFullDay: 'Full day',
  calHalfDay: 'Half day',
  calHourly: 'Hourly',
  calBlocked: 'Blocked',
  calBooked: 'Booked',
  calRequested: 'Requested — not confirmed',
  calBookingCount: { one: '{count} booking', other: '{count} bookings' },
  calNoBookings: 'no bookings',
  calJumpTo: 'Go to a month',
  calPrevMonth: 'Previous month',
  calNextMonth: 'Next month',
  calPrevYear: 'Previous year',
  calNextYear: 'Next year',
  calPickDay: 'Tap a coloured day to see its bookings.',
  calDayFree: 'Nothing booked on this day.',

  // ------------------------------------------------------------ status pills
  statusConfirmed: 'confirmed',
  statusPending: 'requested',
  statusNewRequest: 'new request',
  statusAwaiting: 'awaiting confirmation',
  statusHintNewRequest: 'A guest has asked for this slot. Confirm or decline it.',
  statusHintAwaiting: 'Sent to the venue. They will confirm or decline it.',
  statusHintConfirmed: 'The venue has accepted. The slot is held for this booking.',
  statusHintCompleted: 'The event date has passed.',
  statusHintCancelled: 'Cancelled or declined. The slot is free again.',
  statusHintExpired: 'No reply before the event, so the request lapsed.',
  statusCancelled: 'cancelled',
  statusCompleted: 'completed',
  statusRefunded: 'refunded',
  statusActive: 'active',
  statusSuspended: 'suspended',
  statusVerified: 'verified',
  statusRejected: 'rejected',
  statusNew: 'new',
  statusContacted: 'contacted',
  statusClosed: 'closed',
  statusHandled: 'handled',
  statusExpired: 'expired',

  // -------------------------------------------------------------- venue card
  cardNew: 'New',
  cardDayFree: 'Free all day',
  cardDayPartial: { one: 'Partly booked · {count} session free', other: 'Partly booked · {count} sessions free' },
  cardFullAc: 'Full AC',
  cardParking: 'Parking',
  cardCompare: 'Compare',
  cardAdded: 'Added',
  cardCompareAdd: 'Add to compare',
  cardCompareRemove: 'Remove from compare',
  cardCompareLimit: 'You can compare two venues at a time. Remove one to add another.',

  // ------------------------------------------------------------ browse page
  browseSearchPlaceholder: 'Search by venue name, area or city…',
  browseSearchLabel: 'Search venues',
  browseAvailableOn: 'Available on',
  browseAnyDate: 'Any date',
  browseAllTypes: 'All types',
  browseParking: 'Parking',
  browsePriceDesc: 'Price: high → low',
  browsePriceAsc: 'Price: low → high',
  browseCapacity: 'Capacity',
  browseRating: 'Rating',
  browseClearFilters: 'Clear filters',
  browseMaxPriceRule: 'Enter a price per hour above 0',
  browseFinding: 'Finding available venues…',
  browseLoadFailed: "Couldn't load venues",
  browseVenueCount: { one: '{count} venue', other: '{count} venues' },
  browseCityCount: { one: '{count} city', other: '{count} cities' },
  browseSummary: '{venues} in {cities}, for up to {capacity} guests.',
  browseSummaryRating: 'Average rating: {rating}★.',
  browsePickMore: { one: 'Pick {count} more to compare', other: 'Pick {count} more to compare' },

  // ------------------------------------------------------------- venue page
  detailLoginFirst: 'Log in to send your booking request',
  detailLinkCopied: 'Link copied',
  detailWhatsappMessage: "Hi, I'm interested in booking {name}. Is it available?",
  detailPhotosUpdated: 'Photos updated {date}',
  detailNewListing: 'New listing',
  detailReviewCount: { one: '{count} review', other: '{count} reviews' },
  detailRoomCount: { one: '{count} room', other: '{count} rooms' },
  detailCarCount: { one: '{count} car', other: '{count} cars' },
  detailBikeCount: { one: '{count} bike', other: '{count} bikes' },
  detailAmenities: 'Facilities',
  detailWholeHall: 'You book the whole venue.',
  detailPeakTip: 'Bookings on Saturdays and Sundays cost this much more than the weekday price. Quotes already include it.',
  detailReviews: 'Reviews',
  detailNoReviews: 'No reviews yet.',
  detailCall: 'Call {phone}',
  detailNoPhone: 'No phone number listed.',
  detailDirections: 'Directions',
  detailShare: 'Share',
  detailSimilar: 'Similar venues',
  detailSameBuilding: 'More venues at {name}',
  detailSimilarBody: 'Tap Compare on any card to see it side by side with {name}.',

  // ----------------------------------------------------------- compare modal
  cmpTitle: 'Compare venues',
  cmpSubtitle: 'The better value in each row is highlighted.',
  cmpHourly: 'Hourly price',
  cmpHalfDay: 'Session',
  cmpFullDay: 'Full day',
  cmpGuestRooms: 'Guest rooms',
  cmpOpen: 'Open {name}',

  // ------------------------------------------------------- booking panel etc
  bookHours: { one: '{count}h', other: '{count}h' },
  myQuoteFailed: 'Could not price this slot. Try another time.',
  bookDays: { one: '{count} day', other: '{count} days' },
  bookMinutes: { one: '{count}m', other: '{count}m' },
  bookBuffer: 'Cleaning time',
  bookBufferTip: 'Kept free before and after your slot so bookings do not overlap',
  bookTotalEstimate: 'Estimated total',
  bookNoPayment: 'No payment now',
  bookVenueConfirms: 'Venue confirms first',

  // enquiry form on the venue page
  enqSend: 'Send enquiry',
  enqEventDate: 'Event date',
  enqMessagePlaceholder: 'Tell the venue about your event…',
  enqNamePhoneRequired: 'Enter your name and mobile number',
  enqGuestsRule: 'Guests must be a whole number, 0 or more',
  enqGuestsMax: 'Guests must be {max} or fewer',
  enqMessageRequired: 'Write a short message so the venue knows what you need',
  enqMessageMax: 'Please keep the message under {max} characters',
  enqSent: 'Enquiry sent. The venue will contact you.',

  // gallery + panorama
  galPrev: 'Previous photo',
  galNext: 'Next photo',
  galPhotoAlt: '{name} — photo {index} of {count}',
  panoAria: '360° view. Drag to look around.',
  panoDrag: 'Drag to look around',

  // --------------------------------------------------------- my bookings
  myBookingsTitle: 'My bookings',
  myBookingsSubtitle: 'Track each request, change its date, cancel it, or review the venue after your event.',
  myNoBookings: 'No bookings yet',
  myNoBookingsBody: 'Find a venue and send a request. It will show up here.',
  myReschedule: 'Reschedule',
  myRescheduledPending: 'Moved. The venue will confirm the new time.',
  myLeaveReview: 'Write a review',
  myReview: 'Review',
  myReviewThanks: 'Thanks for your review.',
  myComment: 'Comment',
  mySubmitReview: 'Post review',
  myStarCount: { one: '{count} star', other: '{count} stars' },
  myCurrentlyAt: 'Currently',
  myPickNewSlot: 'Pick a new slot below.',
  myLoadingAvailability: 'Loading availability…',
  myVenueClosedToChanges:
    'This venue is no longer taking bookings, so this one cannot be moved here. Contact the venue to change or cancel it.',
  myNewSlot: 'New slot',
  myDuration: 'Duration',
  myOldAmount: 'Current amount',
  myNewAmount: 'Amount for the new time',
  myAmountUp: 'The new time costs {diff} more.',
  myAmountDown: 'The new time costs {diff} less.',
  myAlreadyPaid: 'You have already paid the venue {paid}; it counts towards the new amount.',
  myReapprovalNote: 'The venue confirms the new time again before it is final.',
  myMoving: 'Rescheduling…',
  myConfirmNewSlot: 'Confirm new slot',
  myPickSlot: 'Pick a slot',
  myCallVenue: 'Call venue',

  // ------------------------------------------------- asking a venue for a slot
  reqAwaitingTitle: 'The venue will confirm this booking',
  reqAwaitingBody:
    'The venue manager will check the date and confirm it. Call or message them to get it confirmed faster.',
  reqRequestNo: 'Request no.',
  reqEstimate: 'Estimated total',
  reqPayAtVenue: 'Pay at the venue. Nothing to pay online.',
  reqNextTitle: 'What happens next',
  reqNext1: '{venue} checks your request and confirms or declines it.',
  reqNext2: 'You hear as soon as they decide, in My bookings and on your phone.',
  reqNext3: 'Once confirmed, you pay the full amount at the venue. Nothing is paid online or in advance.',
  reqQuestions: 'Questions about this request?',
  reqCallVenue: 'Call the venue',
  reqWhatsapp: 'WhatsApp',
  reqWhatsappMessage:
    'Hi, I have requested {venue} for {slot} (request #{id}). Could you please confirm it?',
  reqViewBookings: 'View my bookings',
  reqNotHeldNote: 'The slot is not kept for you until the venue confirms it.',
  reqAlreadySent: 'You have already requested this slot. The venue has not answered yet.',

  // ------------------------------------------------------------ cancellation
  cxlTitle: 'Cancel booking',
  cxlWithdrawTitle: 'Withdraw request',
  cxlNothingAgreed: 'Not confirmed yet, nothing paid',
  cxlStartsIn: 'Starts in',
  cxlPolicyIntro: 'Payments are made to the venue directly, not through Aghosha. If you have paid them, how much they return depends on how soon the event is:',
  cxlPercentBack: '{percent}% back',
  cxlAppliesToYou: 'Applies to you',
  cxlNoRefundWindow:
    'This booking is too close to the event for a refund. Cancelling will not return any money.',
  cxlSlotReleased: 'The slot is freed straight away and someone else can book it.',
  cxlCannotUndo: 'Cancelling cannot be undone.',
  cxlKeepBooking: 'Keep booking',
  cxlKeepRequest: 'Keep request',
  cxlCancelling: 'Cancelling…',
  cxlDoneRefund: 'Cancelled. The venue will return {amount} to you directly.',
  cxlDoneNoRefund: 'Booking cancelled. Nothing is due back to you.',
  refundTier48: '48 hours or more before the event',
  refundTier12: 'Between 12 and 48 hours before',
  refundTier0: 'Less than 12 hours before',
  refundAlreadyStarted: 'already started',
  refundDays: { one: '{count} day', other: '{count} days' },
  refundHours: { one: '{count} hour', other: '{count} hours' },
  refundMinutes: { one: '{count} minute', other: '{count} minutes' },

  // ------------------------------------------------------- shared components
  selectPlaceholder: 'Select…',
  skLoadingVenues: 'Loading venues',
  skLoadingVenue: 'Loading venue',
  pwShow: 'Show password',
  pwHide: 'Hide password',
  dfSelectDate: 'Select a date',
  dfChooseDate: 'Choose a date',
  dfChooseTime: 'Choose a time',
  dfHour: 'Hour',
  dfMinute: 'Min',
  dfMeridiem: 'AM/PM',

  // -------------------------------------------------------------- validation
  valRequired: '{label} is required',
  valNameChars: "{label} can only contain letters, numbers, spaces and . ' -",
  valMobileRequired: 'Mobile number is required',
  valPhoneDigits: 'Must be exactly 10 digits',
  valEmailInvalid: 'Enter a valid email address',
  valPasswordRequired: 'Password is required',
  valPasswordMin: { one: 'Use at least {count} character', other: 'Use at least {count} characters' },
  valIdDigits: { one: '{label} must be {count} digit', other: '{label} must be {count} digits' },
  valIdChars: { one: '{label} must be {count} character', other: '{label} must be {count} characters' },

  // -------------------------------------------------------------------- auth
  authWelcomeBack: 'Welcome back',
  authCreateAccount: 'Create your account',
  authLoginSubtitle: 'Log in to manage your bookings.',
  authLogin: 'Log in',
  authConfirmNumberTitle: 'Confirm your mobile number',
  authConfirmNumberBody: 'We sent a code by SMS to {phone}. Enter it to finish your first login.',
  authCodeLabel: 'Code from SMS',
  authCodeExpiresIn: 'Code expires in {time}',
  authCodeExpired: 'Code expired. Tap Resend code to get a new one.',
  authConfirmAndSignIn: 'Confirm and log in',
  authResendCode: 'Resend code',
  authCodeResent: 'A new code is on its way.',
  authSignup: 'Sign up',
  authLoggingIn: 'Logging in…',
  authRememberMe: 'Remember my number',
  authPhonePlaceholder: 'e.g. 9000000005',
  authPhoneHint: '10-digit mobile number',
  authWelcomeName: 'Welcome, {name}!',
  authManagerPending: 'Account created. You can list venues once an admin approves it.',
  authOtpIntro: 'We\'ll text a 6-digit code to this number.',
  authSendOtp: 'Send code',
  authEnterOtp: 'Code from SMS',
  authOtpPlaceholder: '6-digit code',
  authOtpIs: 'Your code is {code}',
  authOtpSent: 'Code sent to {phone}',
  authOtpStillLive: 'The code sent to {phone} is still valid. Enter it below.',
  authVerify: 'Verify number',
  authResend: 'Resend code',
  authOtpExpiresIn: 'Code expires in {time}',
  authOtpExpired: 'Code expired. Tap Resend code to get a new one.',
  authChangeNumber: 'Change number',
  authVerified: 'Verified',
  authAddress: 'Address',
  authAddressPlaceholder: 'Street, city',
  authCreateAccountAction: 'Create account',
  authIdentityCheck: 'Identity verification',
  authIdentityCheckBody:
    'An admin checks your ID before your account goes live.',
  authIdType: 'ID type',
  authIdNumber: 'ID number',
  authIdNumberPlaceholder: 'as printed on the document',
  authIdUpload: 'Upload the document (PDF, JPG or PNG · max 10 MB)',
  authIdUploaded: 'ID proof uploaded',
  authIdRequired: 'Upload your ID proof and enter its number',
  authUploading: 'Uploading…',

  // suspension + appeals
  authSuspendedTitle: 'Your account has been suspended',
  authSuspendedReason: 'Reason given:',
  authSuspendedNoReason:
    'No reason was given. Send an appeal below and an admin will reply to you.',
  authSuspendedOn: 'Suspended on {date}',
  authAppealDeclined: 'Your last appeal was declined',
  authAppealPending:
    'An admin is reviewing your appeal. You can log in once it is approved.',
  authAppealPendingSent:
    'An admin is reviewing your appeal, sent {date}. You can log in once it is approved.',
  authAppealLabel: 'Your message to the admin',
  authAppealPlaceholder: 'Explain why the suspension should be lifted (at least 10 characters)',
  authSupportingDoc: 'Supporting document',
  authSupportingDocHint: 'optional — PDF, JPG or PNG',
  authSupportingDocTip: 'A renewed licence or a clearer ID usually solves this.',
  authDocAttached: 'Document attached',
  authSendAppeal: 'Send appeal',
  authSending: 'Sending…',
  authAppeal: 'Appeal this suspension',
  authAppealAgain: 'Appeal again',
  authBackToLogin: 'Back to login',
  authMediaKicker: 'Aghosha',
  authQuoteA: 'Find the space.',
  authQuoteB: 'Make the moment.',
  authMediaBody: 'Verified venues, real availability, and nothing paid online.',
  authAccountTabs: 'Account',
  authRolePickLabel: 'I want to',
  authRoleCustomerTitle: 'Book venues',
  authRoleCustomerHint: "For events I'm planning",
  authRoleManagerTitle: 'List my venue',
  authRoleManagerHint: 'For venues I manage',

  // ------------------------------------------------------------------ contact
  ctHours: 'Mon–Sat, 9:00 AM – 7:00 PM',
  ctHoursLabel: 'Hours',
  ctHeroLead: "We're here to",
  ctHeroAccent: 'help.',
  ctHeroBody: 'Questions about booking a venue or listing your own? Call, email or send us a message below.',
  ctReachUs: 'Reach us',
  ctOffice: 'Office',
  ctPhone: 'Phone',
  ctGetDirections: 'Get directions',
  ctSendMessage: 'Send us a message',
  ctSubject: 'Subject',
  ctSubjectHint: 'How can we help?',
  ctMessageHint: 'What do you need help with? For a booking, add the date and guest count.',
  ctNameMessageRequired: 'Enter your name and a message',
  ctThanks: 'Message sent. We will get back to you soon.',

  // ------------------------------------------------------------------ privacy
  // Page chrome only — the clauses themselves live in PrivacyPage.tsx, which
  // says why.
  privTitle: 'Privacy',
  privUpdated: 'Last updated {date}',
  privHeroLead: 'Your details, and',
  privHeroAccent: 'nothing more.',
  privHeroBody:
    'What Aghosha collects when you book or list a venue, who can see it, and how to get it removed.',
  privContactHeading: 'Contact us about privacy',
  privContactBody:
    'Write to our Grievance Officer with any question about this policy, or to ask for a copy, correction or deletion of the data we hold about you.',
  privUseContactForm: 'Use the contact form',

  // ------------------------------------------------------------ notifications
  notifSubtitle: 'Booking updates, approvals and refunds.',
  notifEmpty: 'No notifications yet',
  notifEmptyBody: 'New updates will appear here.',

  // ------------------------------------------------------------ account modals
  epCurrentRequired: 'Enter your current password to change it',
  epSaved: 'Details saved',
  epPhoneFixed: 'Your mobile number cannot be changed.',
  epChangePassword: 'Change password',
  epCurrentPassword: 'Current password',
  epNewPassword: 'New password',
  epNewPasswordHint: 'Leave blank to keep your current one',
  epSaveChanges: 'Save changes',

  daCannotUndo: 'This cannot be undone.',
  daBody:
    'Your name, email, address and mobile number will be erased, and you will not be able to log in again.',
  daManagerNote: 'Your venues will be removed from the site.',
  daCancelFirst: 'You must cancel any upcoming bookings before you can delete your account.',
  daPastBookings: "Past bookings stay in the venue's records, without your details.",
  daTypeToConfirm: 'Type {word} to confirm',
  daKeepAccount: 'Keep my account',
  daDeleting: 'Deleting…',
  daDeleted: 'Account deleted',

  // ------------------------------------------------------------ slot picker
  slotMorning: 'Morning',
  slotAfternoon: 'Afternoon',
  slotEvening: 'Evening',
  slotBySession: 'By session',
  slotByHour: 'By hour',
  slotByDays: 'Several days',
  slotFirstDay: 'First day',
  slotLastDay: 'Last day',
  slotPickLastDay: 'Pick the last day',
  slotDaysHint: 'Each day runs {from} – {to}. Up to {max} days in one booking. The venue checks every day when it confirms.',
  slotFirstDayTaken: 'Part of the first day is already taken. Choose another start date.',
  slotPickDays: 'Pick the first and the last day of your event.',
  slotSessionHeading: 'Session (pick neighbouring sessions to book longer)',
  slotAllSessions: { one: 'all {count} session', other: 'all {count} sessions' },
  slotHours: 'Hours',
  slotHourCount: { one: '{count} hour', other: '{count} hours' },
  slotSelected: 'selected',
  slotPickSession: 'Pick a session, or the whole day, to see the price.',
  slotPickHours: 'Tap an hour to book it. To book longer, tap a second hour: every hour in between is included.',
  slotSelectedRange: 'Selected {from} – {to}.',
  slotRequested: 'Also requested',
  slotCrossesTaken: 'That time includes an hour that is not available. Choose a different time.',
  slotTurnaround: 'Cleaning time',
  slotDayPartlyBooked: 'Not available. Part of the day is booked.',
  slotDayBlocked: 'Not available. The venue is closed that day.',
  slotBlockedAllDay: 'The venue is closed all day',
  slotBlockedPartOfDay: 'Part of this day is closed',

  // --------------------------------------------------------- manager console
  mgrDashboard: 'Manager dashboard',
  mgrDashboardSubtitle: 'Your venues, bookings and revenue.',
  mgrSupervisorDashboard: 'Supervisor dashboard',
  mgrSupervisorSubtitle: 'The venues you look after, and their bookings.',
  mgrAwaitingApproval: 'Awaiting approval',
  mgrAwaitingApprovalBody: 'An admin must approve your manager account before you can list venues.',
  mgrTabOverview: 'Overview',
  mgrTabHalls: 'Venues',
  mgrTabBookings: 'Bookings',
  mgrTabEnquiries: 'Enquiries',
  mgrTabTeam: 'Supervisors',
  mgrTabAssets: 'Assets',

  // ------------------------------------------------------------------ assets
  astTitle: 'Assets',
  astSubtitle: 'What this venue owns, and whether it works.',
  astPickVenue: 'Venue',
  astAdd: 'Add an item',
  astEdit: 'Edit item',
  astNone: 'No items listed yet',
  astNoneBody: 'Add the equipment this venue owns, such as projectors, chairs and sound systems, to keep track of its condition.',
  astNoneReadOnly: 'Nothing has been listed for this venue yet.',
  astSummary: {
    one: '{count} item',
    other: '{count} items',
  },
  astNeedsAttention: {
    one: '{count} needs attention',
    other: '{count} need attention',
  },
  astAllWell: 'All in order',
  astColItem: 'Item',
  astColQty: 'Qty',
  astColStatus: 'Status',
  astColChecked: 'Last checked',
  astSearch: 'Search items…',
  astFilterAll: 'Any condition',
  astShowRetired: 'Show retired',
  astName: 'What is it?',
  astNamePlaceholder: 'Projector, banquet chair, sound system…',
  astQuantity: 'How many does the venue own?',
  astAffected: 'How many are affected?',
  astNote: 'Note (optional)',
  astNotePlaceholder: 'bulb replaced, spare in the store cupboard',
  astStatusWorking: 'Working',
  astStatusMaintenance: 'Under maintenance',
  astStatusDamaged: 'Damaged',
  astStatusRetired: 'Retired',
  /** e.g. "1 under maintenance" — some of the group, not all of it. */
  astSomeAffected: '{count} {status}',
  astCheckedOn: 'Checked {date}',
  astMarkAs: 'Mark as',
  astSaved: 'Item saved',
  astRemoved: 'Item removed',
  astRemoveTitle: 'Remove this item?',
  astRemoveBody: 'This removes {name} from {venue}, along with its history. If the item is just gone, mark it retired instead to keep the record.',
  astRemoveConfirm: 'Remove item',
  astAllVenues: 'All venues',
  astColVenue: 'Venue',
  astCategory: 'Category',
  astAnyCategory: 'Any category',
  astNoCategory: 'Not set',
  astCatFurniture: 'Furniture',
  astCatAudioVisual: 'Audio & visual',
  astCatLighting: 'Lighting',
  astCatElectrical: 'Electrical',
  astCatClimate: 'AC & fans',
  astCatKitchen: 'Kitchen & catering',
  astCatDecor: 'Decor',
  astCatSafety: 'Safety',
  astCatCleaning: 'Cleaning',
  astCatOther: 'Other',
  astLocation: 'Where in the venue (optional)',
  astLocationPlaceholder: 'Stage, store room, dining hall…',
  astBrand: 'Make / model (optional)',
  astBrandPlaceholder: 'Epson EB-X51',
  astSerial: 'Serial no. / asset tag (optional)',
  astSerialPlaceholder: 'SN-0042',
  astSerialShort: 'S/N',
  astPurchasedOn: 'Bought on (optional)',
  astWarrantyLabel: 'Warranty until (optional)',
  astNotRecorded: 'Not recorded',
  astWarrantyUntil: 'Warranty until {date}',
  astWarrantyEnded: 'Warranty ended {date}',
  astPurchaseFuture: 'The purchase date cannot be in the future',
  astWarrantyBeforePurchase: 'The warranty cannot end before the item was bought',
  astMoveNote: 'This moves the item out of {from}.',
  astReadOnlyHint: 'Your manager has not given you permission to add or remove items. You can still change an item\'s condition.',

  mgrCalendarTitle: 'Booking calendar',
  mgrNoVenuesTitle: 'No venues yet',
  mgrNoVenuesBody: 'Add your first venue and its bookings will show up here.',
  mgrNoVenuesSupervisor: 'Your manager has not assigned you a venue yet.',

  mgrRevenueNet: 'Net revenue (last 12 months)',
  mgrRevenue: 'Revenue',
  mgrConfirmed: 'Confirmed',
  mgrPendingApproval: 'New requests',
  mgrReportTitle: 'Booking report',
  mgrReportCardTitle: 'All bookings at your venues',
  mgrReportCardBody: 'One row per booking: venue, guest name and phone, date and time, status, payment and amount.',
  mgrReportPdf: 'Download PDF',
  mgrReportCsv: 'Download Excel (CSV)',
  mgrRevenue12: 'Revenue — last 12 months',
  mgrRevenue12Subtitle: 'Money received each month, minus refunds.',
  mgrRevenueByVenue: 'Revenue by venue',
  mgrRevenueByVenueSubtitle: 'Money received at each venue, minus refunds.',
  mgrShare: 'Share of revenue',
  mgrPerVenue: 'By venue',
  mgrPerVenueSubtitle: 'Bookings and net revenue for each venue.',
  mgrPopularHours: 'Most-booked hours',
  mgrPopularHoursSubtitle: 'The start times guests book most, from confirmed and completed bookings.',
  mgrNoData: 'No bookings yet',

  mgrYourVenues: 'Your venues',
  mgrListedCount: { one: '{count} listed', other: '{count} listed' },
  mgrAddHall: 'Add venue',
  mgrVerified: 'verified',
  mgrPendingVerification: 'pending verification',
  mgrPendingVerificationBody:
    'An admin is checking your documents. Guests cannot see this venue until it is verified.',
  mgrVerificationRejected: 'verification rejected',
  mgrRejected: 'Rejected',
  mgrRejectedBody: 'Edit the venue and upload the corrected documents to send it again.',
  mgrInactive: 'inactive',
  mgrPerHour: '{price}/hr',
  mgrGuestCount: { one: '{count} guest', other: '{count} guests' },
  mgrBuffer: '{minutes} min cleaning time',
  mgrBookForCustomer: 'Book for a guest',
  mgrBlockDates: 'Block dates',
  mgrNoHallsYet: 'No venues yet',
  mgrNoHallsYetBody: 'Add your first venue to start taking bookings.',
  mgrDeleteHallTitle: 'Delete this venue?',
  mgrDeleteHallBody: '{name} and its listing will be removed. Existing bookings are not affected.',
  mgrDeleted: 'Deleted',

  mgrNewBooking: 'New booking',
  mgrGuest: 'Guest',
  mgrWhen: 'When',
  mgrBookedFor: 'For',
  mgrBookedOn: 'Booked',
  mgrPayment: 'Payment',
  mgrReceipt: 'Receipt',
  mgrTxn: 'Txn',
  mgrPaidAtVenue: 'Collected at the venue',
  mgrPaidOnlineLegacy: 'Paid online (older booking)',
  mgrPaid: 'Paid',
  mgrAtVenue: 'at venue',
  mgrFilterAnyStatus: 'Any status',
  mgrFilterFrom: 'From',
  mgrFilterTo: 'To',
  mgrFilterAnyDate: 'Any date',
  mgrFilterGuest: 'Guest',
  mgrFilterGuestHint: 'Name or mobile number',
  mgrFilterClear: 'Clear filters ({count})',
  mgrBookedAtVenueTip: 'Booked at the venue',
  mgrApprove: 'Approve',
  mgrApproveBooking: 'Approve booking',
  mgrReject: 'Reject',
  mgrRejectBooking: 'Reject booking',
  mgrNoBookings: 'No bookings',
  mgrAwaitingCount: {
    one: '{count} awaiting your confirmation',
    other: '{count} awaiting your confirmation',
  },
  mgrReceiptTip: 'Download receipt',
  mgrCancelBookingTitle: 'Cancel this booking?',
  mgrCancelBookingBody: 'The slot is freed and the guest is told. Anything you recorded as paid is marked as returned in full — hand that money back to the guest yourself; Aghosha does not move payments.',
  mgrCancelBookingAction: 'Cancel booking',
  mgrDone: 'Done',
  mgrUpdated: 'Updated',

  // -------------------------------------------------------------- venue form
  hallAddTitle: 'Add venue',
  hallEditTitle: 'Edit venue',
  hallRequiredHintBefore: 'Fields marked',
  hallRequiredHintAfter: 'are required.',
  hallSecBasics: 'Basics',
  hallSecLocation: 'Location',
  hallSecCapacity: 'Capacity & facilities',
  hallSecPhotos: 'Photos',
  hallSec360: '360° view',
  hallSec360Body:
    'Guests can drag the photo to look around the venue.',
  hallSecDocuments: 'Legal documents',
  hallSecDocumentsBody:
    'To show this venue is allowed for public use, upload its Building Completion / Occupancy Certificate and Premises / Public Amusement Licence. An admin checks these before your venue goes live.',
  hallSecPricing: 'Pricing',
  hallSecAvailability: 'Availability',

  hallName: 'Name',
  hallCategory: 'Category',
  hallBuilding: 'Building / auditorium complex',
  hallBuildingNone: 'Not part of a building (single venue)',
  hallBuildingNew: 'New building',
  hallBuildingNameHint: 'e.g. Skyline Convention Centre',
  hallBuildingHint: 'Use this when halls share one address, like a main hall and a mini hall. Guests see the other halls on each hall\'s page.',
  bldTitle: 'Buildings',
  bldIntro: 'A building groups venues that share one address, like a main hall and a mini hall. Choose the building on each venue.',
  bldNone: 'No buildings yet.',
  bldAdded: 'Building added.',
  bldRenamed: 'Building renamed.',
  bldRemoved: 'Building removed. Its venues are now listed on their own.',
  bldRemoveTip: 'Removes the grouping only. The venues stay.',
  bldHallCount: { one: '{count} venue', other: '{count} venues' },
  hallContactPhone: 'Contact phone',
  hallDescription: 'Description',
  hallAddress: 'Address (street)',
  hallLocality: 'Locality',
  hallCity: 'City',
  hallPincode: 'Pincode',
  hallSeatedCapacity: 'Seated capacity',
  hallFloatingCapacity: 'Floating capacity',
  hallAcRooms: 'AC rooms',
  hallAirConditioning: 'Air conditioning',
  hallAc: 'AC',
  hallNonAc: 'Non-AC',
  hallLift: 'Lift',
  hallAvailable: 'Available',
  hallNotAvailable: 'Not available',
  hallCarParking: 'Car parking',
  hallBikeParking: 'Bike parking',
  hallCatering: 'Catering',
  hallCuisine: 'Cuisine',
  hallEstablished: 'Established (year)',
  hallSeatingLayout: 'Seating layout',
  hallPerHour: '₹ / hour',
  hallPerHalfDay: '₹ / session',
  hallPerDay: '₹ / day',
  hallPeakMultiplier: 'Weekend price (× usual rate)',
  hallBufferMinutes: 'Cleaning time (minutes)',
  hallSessionTimes: 'Session times',
  hallSessionTimesHint:
    'The morning, afternoon and evening times guests can book. Leave a gap of at least your cleaning time between them, or one booking will block the next.',
  hallSessionStart: '{label} start',
  hallSessionEnd: '{label} end',
  hallSessionEndsFirst: '{label} must end after it starts.',
  hallAfternoonBeforeMorning: 'Afternoon cannot start before morning ends.',
  hallEveningBeforeAfternoon: 'Evening cannot start before afternoon ends.',
  hallBufferGap:
    'With {minutes} minutes of cleaning time, leave at least that much time between sessions. Otherwise, booking one session will block the next.',
  hallTo: 'to',
  hallMidnight: '{time} (midnight)',
  hallBookableBy: 'Bookable by',
  hallAllowHourly: 'Allow hourly booking',
  hallAllowHourlyHint:
    'Guests can always book a session (morning, afternoon or evening). Turn this on to also let them book by the hour.',
  hallListed: 'Listed',
  hallSave: 'Save venue',
  hallSaved: 'Saved',
  hallSubmitted: 'Venue sent to an admin for verification.',
  hallFillRequired: 'Check the highlighted fields and try again.',
  hallCapacityRule: 'Must be a whole number of 1 or more',
  hallNotNegative: 'Cannot be negative',
  hallNumRange: 'Enter a number between {min} and {max}',
  hallPincodeRule: 'Pincode must be 6 digits',
  hallPriceRange: 'Enter an amount between ₹0 and ₹{max}',
  hallPriceMin: 'Enter at least ₹{min}, or 0 if you do not offer this rate',
  hallHourlyPriceRequired: 'Hourly booking is on, so set a price per hour above ₹0',
  hallPriceRequired: 'Set at least one price above ₹0',
  hallSessionPriceRequired: 'Hourly booking is off, so set a session or full-day price above ₹0',
  hallDocOccupancyHint: 'Issued by your local municipal corporation',
  hallDocLicenseHint: 'Issued by the municipal corporation or police commissionerate',
  hallDocUploadRequired: 'Upload this document',

  // government ID types offered at manager signup
  idAadhaar: 'Aadhaar',
  idPan: 'PAN card',
  idPassport: 'Passport',
  idDrivingLicence: 'Driving licence',
  idVoterId: 'Voter ID',
  hallDocRefRequired: 'Enter the certificate / licence number',
  hallDocAuthorityRequired: 'Enter the issuing authority',

  // seating layouts — the value stored stays English; only the label follows
  layoutNotSpecified: 'Not specified',
  layoutFlexible: 'Flexible / whole hall',
  layoutTheatre: 'Theatre style',
  layoutBanquet: 'Banquet (round tables)',
  layoutClassroom: 'Classroom',
  layoutUShape: 'U-shape',
  layoutTiered: 'Fixed tiered seating',
  layoutMultiple: 'Multiple halls',

  // catering + cuisine, shared with the public venue page
  cateringNone: 'Not available',
  cateringInhouse: 'In-house only',
  cateringOutside: 'Outside allowed',
  cateringBoth: 'In-house & outside',
  cuisineVeg: 'Veg',
  cuisineNonveg: 'Non-veg',
  cuisineBoth: 'Veg & non-veg',

  // ---------------------------------------------------------- venue booking
  vbNoVenues: 'Add a venue first. There is nothing to book yet.',
  vbNoneBookable:
    'None of your venues can take bookings yet. A venue must be verified by an admin and listed before you can book it.',
  vbIntro:
    'For a guest booking in person or by phone. The booking is confirmed straight away, and the slot can no longer be booked online.',
  vbHidden: {
    one: '{hidden} of your {total} venues is not shown. Only verified, listed venues can be booked.',
    other: '{hidden} of your {total} venues are not shown. Only verified, listed venues can be booked.',
  },
  vbCustomerName: 'Guest name',
  vbCustomerMobile: 'Guest mobile',
  vbCustomerMobileHint: 'If they already have an account, this booking will show up in it.',
  vbCustomerEmail: 'Guest email',
  vbNote: 'Note',
  vbNoteHint: 'e.g. birthday, 120 guests',
  vbPaymentCollected: 'Payment collected at the venue',
  vbBooking: 'Booking…',
  vbBookMarkPaid: 'Book & mark paid',
  vbBookUnpaid: 'Book (unpaid)',
  vbBookedClaimable: "Booked for {name}. They'll see it when they sign up with {phone}.",
  vbBookedExisting: 'Booked for {name}. It is in their account now.',

  // ------------------------------------------------------------- supervisors
  supSubtitle: 'Staff who run the venues you assign to them. They cannot see revenue, or add or delete venues.',
  supAdd: 'Add supervisor',
  supEditTitle: 'Edit {name}',
  supNewPassword: 'New password',
  supNewPasswordHint: 'Leave blank to keep the current password',
  supPhoneRecheckNote: 'They will confirm the new number with an SMS code at their next sign-in.',
  supUpdated: 'Supervisor updated.',
  supUpdatedNewPhone: 'Supervisor updated. They will confirm the new number when they next log in.',
  supCreatedFirstLogin: 'On their first sign-in they confirm this number with a code sent to it by SMS.',
  supMobile: 'Mobile',
  supCanEdit: 'Can edit',
  supAdded: 'Added',
  supNoneAssigned: 'none assigned',
  supNone: 'No supervisors yet. Add one and assign them a venue.',
  supNoVenues: 'You have no venues to assign yet.',
  supDelisted: 'delisted',
  supRemoved: 'Supervisor removed',
  supRemoveTitle: 'Remove this supervisor?',
  supRemoveBody: '{name} will no longer be able to log in.',
  supCanEditOn: '{name} can now edit their venues',
  supCanEditOff: '{name} can no longer edit venues',
  supCanEditAria: 'Allow {name} to edit venue details',
  supCanEditLabel: 'Can edit venue details',
  supCanEditHint:
    "Lets them change the venue's details, prices, photos and documents. They can always block dates and take bookings for venues assigned to them.",
  supAssignIntro: '{name} can see and manage only the venues ticked here.',
  supAssign: 'Assign venue',
  supAssigning: 'Assigning…',
  supHallsUpdated: 'Venues updated',
  supHallsLabel: 'Venues they look after',
  supPermissions: 'Permissions',
  supFullName: 'Full name',
  supPasswordHint: 'Use at least 6 characters',
  supPasswordNote: 'Choose a password and give it to them.',
  supCreate: 'Create supervisor',
  supCreating: 'Creating…',
  supCreatedTitle: 'Supervisor created',
  supCreatedBody: 'Give these login details to {name}. The password will not be shown again.',
  supCreatedAssigned: 'They log in on the usual login page and see only the venues you assigned.',
  supCreatedUnassigned: 'No venues assigned yet. They will see nothing until you assign one.',

  // --------------------------------------------------------------- uploaders
  upDragPhotos: 'Drag photos here',
  upOrBrowse: 'or click to choose',
  upPano: 'Upload a 360° photo',
  upMakeCover: 'Set as cover photo',
  upRemovePhoto: 'Remove photo',
  upTooManyPhotos: 'A venue can have at most {max} photos',
  upOnlyFirst: { one: 'Only the first image was added', other: 'Only the first {count} images were added' },
  upReplace: 'Replace',
  upUploadFile: 'Upload file',
  upCover: 'Cover',
  upNoFileYet: 'No file uploaded yet',
  upViewFile: 'View file',
  upCertNumber: 'Certificate / licence number',
  upIssuingAuthority: 'Issuing authority',

  // ------------------------------------------------------------------ charts
  chartNoMonthly: 'No revenue in the last 12 months yet.',
  chartNoVenue: 'No venue revenue yet.',
  chartMonthlyAria: 'Net revenue by month',
  chartVenueAria: 'Net revenue by venue',

  // -------------------------------------------------------------- block dates
  blkFrom: 'From',
  blkTo: 'To',
  blkReason: 'Reason',
  blkReasonHint: 'Maintenance / holiday / private event',
  blkAdd: 'Add block',
  blkBlocked: 'Blocked',
  blkInvalidRange: 'Pick a start and an end, with the end after the start.',
  blkExisting: 'Blocked dates',
  blkNone: 'None',
  blkRemove: 'Remove block',
  blkBlockedRange: 'Blocked {range}. Guests cannot book this time.',
  mgrToastApproved: 'Booking #{id} confirmed. The guest has been notified.',
  mgrToastDeclined: 'Request #{id} declined. The guest has been notified.',
  mgrToastCancelled: 'Booking #{id} cancelled. The guest has been notified.',
  mgrToastVenueDeleted: '{name} deleted.',
  mgrToastEnquiryContacted: 'Enquiry marked as contacted.',
  mgrToastEnquiryClosed: 'Enquiry closed.',
  hallSavedNamed: 'Changes to {name} saved.',
  blkOverlap: 'This time is already blocked. Pick a time outside the blocks listed below.',
  blkRemoveTitle: 'Remove this block?',
  blkRemoveBody: '{range} will open for bookings again.',
  blkRemoveAction: 'Remove block',
  blkBookingsOn: 'Bookings on {date}',
  blkNothingBooked: 'Nothing booked. You can block the whole day.',
  blkInsideWindow: 'inside the time you are blocking',
  blkClashWarning: {
    one: 'Blocking this time will not cancel that booking. It only stops new ones. Cancel it first if the venue really is not available.',
    other:
      'Blocking this time will not cancel those bookings. It only stops new ones. Cancel them first if the venue really is not available.',
  },

  // ------------------------------------------------------------------ enquiries
  enqFrom: 'From',
  enqEvent: 'Event',
  enqMessage: 'Message',
  enqMarkContacted: 'Mark contacted',
  enqNone: 'No enquiries yet',

  // ----------------------------------------------------------- admin console
  admNavManagers: 'Managers',
  admNavUsers: 'Users',
  admNavMessages: 'Messages',
  admNavAudit: 'Audit log',
  mgrTabSubscription: 'Subscription',
  admNavSubscriptions: 'Subscriptions',
  subBannerExpired: 'Your subscription has ended, so your venues are hidden from guests. Your bookings are safe. Renew to list them again.',
  subBannerTrial: { one: 'Your free month ends tomorrow ({date}). Choose a plan to keep your venues listed.', other: 'Your free month ends in {count} days ({date}). Choose a plan to keep your venues listed.' },
  subBannerEnding: { one: 'Your subscription ends tomorrow ({date}). Renew to keep your venues listed.', other: 'Your subscription ends in {count} days ({date}). Renew to keep your venues listed.' },
  subRenew: 'Renew',
  subSeePlans: 'See plans',
  subStateExpired: 'Expired',
  subStateTrial: 'Free month',
  subStateActive: '{plan} plan',
  subStateNone: 'Not started',
  subEndedOn: 'Ended on {date}',
  subListedUntil: 'Venues listed until {date}',
  subDaysLeft: { one: '1 day left', other: '{count} days left' },
  subExpiredBody: 'Your venues are hidden from guests and take no new requests. You can still see and manage your bookings. Pay for a plan below to list them again.',
  subRenewEarlyBody: 'Renewing early loses nothing: a new plan starts when the current one ends.',
  subPlans: 'Plans',
  subPerMonth: 'per month',
  subForMonths: { one: 'for 1 month', other: 'for {count} months' },
  subMonthly: '{price} a month',
  subHowToPay: 'How to pay',
  subAfterPaying: 'After paying, call or email us with the transaction ID and your registered phone number. We record the payment and your plan starts straight away.',
  subPayUpi: 'Pay by UPI',
  subPayBank: 'Pay by bank transfer',
  subUpiId: 'UPI ID',
  subPayee: 'Paid to',
  subPayWithUpiApp: 'Pay with a UPI app',
  subAccountName: 'Account name',
  subAccountNumber: 'Account number',
  subIfsc: 'IFSC',
  subBank: 'Bank',
  subBankName: 'Bank name',
  subBranch: 'Branch',
  subCopy: 'Copy',
  subCopyLabel: 'Copy {label}',
  subCopied: '{label} copied.',
  subCopyFailed: 'Could not copy. Please select and copy it by hand.',
  subPaymentDetails: 'Payment details',
  subPaymentIntro: 'Shown to managers under "How to pay" in their Subscription tab. Fill in UPI, bank transfer, or both.',
  subPaymentNote: 'Note for managers',
  subPaymentNoteHint: 'For example: add your phone number in the payment remark.',
  subSavePayment: 'Save payment details',
  subPaymentSaved: 'Payment details saved. Managers see them now.',
  subHowToPayBody: 'Pay Aghosha by UPI, bank transfer or cash, then call or email us with your registered phone number. We record the payment and your plan starts straight away.',
  subHistory: 'History',
  subFreeMonth: 'Free month',
  subFree: 'Free',
  subPillActive: 'Paid',
  subPillEnding: 'Ending in 7 days',
  subAdminIntro: 'Managers get their first month free, then pay for a plan. A payment you record starts when the manager\'s current period ends. When their time runs out, their venues are hidden from guests until they renew.',
  subManagers: 'Managers',
  subSearch: 'Name, phone or email',
  subAllManagers: 'All managers ({count})',
  subNoManagers: 'No managers match.',
  subVenues: { one: '1 venue', other: '{count} venues' },
  subUntilDays: { one: 'Until {date} · 1 day left', other: 'Until {date} · {count} days left' },
  subNotStarted: 'Starts when the account is approved',
  subRecordPayment: 'Record payment',
  subRecordPaymentFor: 'Record a payment from {name}',
  subPlanSaved: 'Plan saved.',
  subPlanHidden: 'Plan hidden from managers.',
  subPlanShown: 'Plan offered to managers again.',
  subPrice: 'Price (₹)',
  subMonthsLabel: 'Months',
  subOneMonth: '1 month',
  subPlanOff: 'hidden',
  subHidePlan: 'Hide',
  subShowPlan: 'Offer again',
  subRecorded: 'Payment recorded. {name} is listed until {date}.',
  subPlan: 'Plan',
  subAmountReceived: 'Amount received (₹)',
  subMethod: 'Paid by',
  subMethodUpi: 'UPI',
  subMethodBank: 'Bank transfer',
  subMethodCash: 'Cash',
  subMethodCheque: 'Cheque',
  subMethodOther: 'Other',
  subReference: 'Reference',
  subReferenceHint: 'UPI transaction ID, cheque number…',
  subNote: 'Note',
  subWillRun: 'This plan will run from {from} to {to}.',
  subCancelled: 'Payment cancelled.',
  subHistoryFor: '{name}: subscription history',
  subNoHistory: 'Nothing recorded yet.',
  subCancelPayment: 'Cancel payment',
  subCancelBody: 'Only for a payment recorded by mistake. Its period is removed and the manager\'s end date moves back to their last remaining period.',
  admNavMasterData: 'Master data',
  mdIntro: 'Options offered on the venue, booking and equipment forms. Removing an item hides it from forms and filters; records already using it keep it, and you can restore it.',
  mdVenueTypes: 'Venue types',
  mdVenueTypesHint: 'The categories a manager picks for a venue, and guests filter by.',
  mdEventTypes: 'Event types',
  mdEventTypesHint: 'The kinds of event a guest picks when requesting a booking. Guide pages cover the built-in types only.',
  mdSeatingLayouts: 'Seating layouts',
  mdSeatingLayoutsHint: 'Layouts a manager picks on the venue form. Shown on the venue page.',
  mdAmenities: 'Amenities',
  mdAmenitiesHint: 'What a venue offers. Managers tick these on the venue form.',
  mdAssetCategories: 'Equipment categories',
  mdAssetCategoriesHint: "How managers group the equipment in their venue's assets list.",
  mdUsedVenues: { one: 'Used by {count} venue', other: 'Used by {count} venues' },
  mdUsedBookings: { one: 'On {count} booking', other: 'On {count} bookings' },
  mdUsedAssets: { one: 'On {count} item of equipment', other: 'On {count} items of equipment' },
  mdNameEn: 'Name (English)',
  mdNameMl: 'Name (Malayalam)',
  mdIcon: 'Icon (Remix icon name)',
  mdNoMalayalam: 'No Malayalam name: English is shown',
  mdNameRequired: 'Enter the English name.',
  mdAdd: 'Add',
  mdAdded: 'Added to forms and filters.',
  mdEdit: 'Edit',
  mdSaved: 'Saved.',
  mdRemove: 'Remove',
  mdRemoved: 'Removed from forms and filters. Existing records keep it.',
  mdRestore: 'Restore',
  mdRestored: 'Restored to the forms and filters.',
  mdRemovedTitle: 'Removed ({count})',
  admPlatformAdmin: 'Platform administration',

  // admin sign-in
  admSignIn: 'Log in',
  admSigningIn: 'Logging in…',
  admSignInConsole: 'Log in to admin console',
  admAccessOnly: 'Administrator access only.',
  admWrongAccount: 'Wrong account',
  admSignedInAs: 'You are logged in as',
  admNeedsAdmin: 'The admin console needs an admin account.',
  admSignOutSwitch: 'Log out and use an admin account',
  admBackToSite: 'Back to {name}',

  // overview
  admBookers: 'Guests',
  admGmv: 'Booking value',
  admInbox: 'Inbox',
  admSuspendedCount: { one: '{count} suspended', other: '{count} suspended' },
  admAwaitingApprovalCount: { one: '{count} awaiting approval', other: '{count} awaiting approval' },
  admAwaitingDecisionCount: {
    one: '{count} awaiting a decision',
    other: '{count} awaiting a decision',
  },
  admLiveCount: { one: '{count} live', other: '{count} live' },
  admRefunded: '{amount} refunded',
  admMessageCount: { one: '{count} message', other: '{count} messages' },
  admEnquiryCount: { one: '{count} enquiry', other: '{count} enquiries' },
  admNewCount: { one: '{count} new', other: '{count} new' },
  admUnhandledCount: { one: '{count} unhandled', other: '{count} unhandled' },
  admActionCount: { one: 'Last {count} action', other: 'Last {count} actions' },
  admTopVenues: 'Top venues by revenue',
  admNoVenues: 'No venues yet.',

  // shared table bits
  admContact: 'Contact',
  admAccount: 'Account',
  admAccountNumber: 'Account #{id}',
  admIdNumber: 'ID #{id}',
  admNoEmail: 'no email',
  admNetSpend: 'Spent (net)',
  admJoined: 'Joined',
  admRegistered: 'Registered',
  admRegisteredOn: 'registered {date}',
  admReceived: 'Received',
  admSlot: 'Slot',
  admPayment: 'Payment',
  admAnyStatus: 'Any status',
  admActive: 'Active',
  admSuspended: 'Suspended',
  admNoDate: 'no date',
  admDocument: 'document',
  admNumber: 'Number:',
  admAuthority: 'Authority:',
  admUploaded: 'Uploaded:',
  admGuest: 'guest',
  admActor: 'Done by',
  admAction: 'Action',
  admEntity: 'Entity',
  admDetails: 'Details',
  admUnknown: 'unknown',
  admUnknownRole: 'unknown role',
  admCurrently: 'currently',

  // searches + empty states
  admSearchManagers: 'Name, mobile, email or address',
  admSearchUsers: 'Name, mobile or email',
  admSearchBookings: 'Venue, guest or mobile',
  admSearchVenues: 'Venue, category, city or manager',
  admNoManagerMatch: 'No managers match that search.',
  admNoAccountMatch: 'No accounts match those filters.',
  admNoBookingMatch: 'No bookings match those filters.',
  admNoVenueMatch: 'No venues match those filters.',
  admManagerCount: { one: '{count} manager', other: '{count} managers' },
  admAccountCount: { one: '{count} account', other: '{count} accounts' },

  // manager decisions
  admDocuments: 'Documents',
  admVenueDocuments: 'Venue documents',
  admNoVenuesYet: 'No venues added yet.',
  admRemove: 'Remove',
  admRemoveAccount: 'Remove account',
  admRemoveTitle: 'Remove {name}?',
  admRemoveManagerBody: "{name}'s account ({phone}) will be closed and its details erased. Their venues will be removed from the site, and any supervisors they added will lose access. The phone number can be used to sign up again. Past bookings are kept. This cannot be undone.",
  admAccountRemoved: 'Account removed',
  admRemoveUserBody: "{name}'s account ({phone}) will be closed and its details erased. The phone number can be used to sign up again. Past bookings and reviews are kept. This cannot be undone.",
  admIdProof: 'ID proof',
  admNoIdProof: 'none',
  admNoIdProofTip: 'Registered before ID proof was required',
  admNoIdProofBody:
    'No ID proof on file. This manager registered before ID proof was required. Ask them for it before approving.',
  admReview: 'Review',
  admApproving: 'Approving…',
  admApproveManager: 'Approve manager',
  admManagerApproved: 'Manager approved',
  admManagerSuspended: 'Manager suspended and logged out',
  admSuspend: 'Suspend',
  admSuspending: 'Suspending…',
  admSuspendAccount: 'Suspend account',
  admSuspendTitle: 'Suspend {name}',
  admSuspendBody:
    'This logs {name} out everywhere straight away and stops them logging in. They will see the reason below the next time they try to log in, and can appeal.',
  admSuspendReason: 'Reason for suspension (required)',
  admSuspendHintManager: 'e.g. Venue licence expired and was not renewed after two reminders.',
  admSuspendHintUser: 'e.g. Repeated no-shows for confirmed bookings.',
  admSuspendNote: 'Write it for them to read. It is the only explanation they get.',
  admReactivate: 'Reactivate',
  admReactivated: 'Account reactivated',
  admAccountSuspended: 'Account suspended and logged out',

  // bookings
  admCancelled: 'Cancelled',
  admCancelledRefund: 'Cancelled · {amount} refunded',
  admCancelPrompt: 'Cancel {venue} on {date} for {guest}?',
  admCancelRefundsAll: 'The guest gets the full {amount} back and is notified.',
  admCancelNothingPaid: 'Nothing was paid, so there is no refund. The guest is notified.',
  admKeepIt: 'Keep it',

  // enquiries + messages
  admMarked: 'Marked {status}',
  admMarkAs: 'Mark {status}',
  admMarkHandled: 'Mark handled',
  admMarkedHandled: 'Marked handled',
  admReopen: 'Reopen',
  admReopened: 'Reopened',
  admReply: 'Reply',
  admYourMessage: 'your message',
  admNoSubject: 'No subject',
  admNoMessages: 'No messages yet. Messages from the Contact page and suspension appeals appear here.',
  admReinstate: 'Reinstate account',
  admAccountRestored: 'Account restored. They can log in again.',
  admDeclineAppeal: 'Decline appeal',
  admDeclining: 'Declining…',
  admAppealDeclined: 'Appeal declined. They will see your reason when they log in.',
  admDeclineBody:
    'The account stays suspended. {name} will see this note the next time they try to log in, and can appeal only once more, so say what would change your mind.',
  admDeclineReason: 'Why the appeal is declined (required)',
  admDeclineHint: 'e.g. The licence you attached is still the expired one — send the renewed copy.',
  admNoLongerSuspended: 'This account is no longer suspended.',

  // venue moderation
  admVerification: 'Verification',
  admListing: 'Listing',
  admListed: 'listed',
  admVerify: 'Verify',
  admVerifying: 'Verifying…',
  admVerifyVenue: 'Verify venue',
  admVerifyIntro:
    'Check the Occupancy Certificate and Public Amusement Licence below to confirm this venue is legally allowed for public use.',
  admRejecting: 'Rejecting…',
  admConfirmRejection: 'Confirm rejection',
  admRejectReason: 'Reason for rejection (shown to the manager)',
  admRejectReasonHint:
    'e.g. The Occupancy Certificate has expired or cannot be read. Please upload a valid copy.',
  admRejectReasonRequired: 'Enter a reason for the rejection.',
  admVenueVerified: 'Venue verified. It can now go live.',
  admVenueRejected: 'Venue rejected',
  admVenueListed: 'Venue listed',
  admVenueDelisted: 'Venue delisted',
  admDelist: 'Delist',
  admRelist: 'Relist',
  admShowAll: 'Show all',
  admShowPending: 'Show pending only',
  admVenueCountFiltered: '{shown} of {total} venues',
  admVisibleOnBrowse: { one: '{count} visible to guests', other: '{count} visible to guests' },
  admAwaitingVerification: {
    one: '{count} awaiting verification',
    other: '{count} awaiting verification',
  },
  admDocCount: { one: '{count} doc', other: '{count} docs' },
  admLoadingDocuments: 'Loading documents…',
  admNoDocuments: 'No documents on file. This venue was added before documents were required.',
  admDocOccupancy: 'Building Completion / Occupancy Certificate (CC/OC)',
  admDocLicense: 'Premises / Public Amusement Licence',
  admLastDecided: 'last decided {date}',
  admPreviousRejection: 'Previous rejection:',

  // review moderation
  admLoadingReviews: 'Loading reviews…',
  admNoReviews: 'This venue has no reviews.',
  admReviewModerationNote:
    'Remove reviews that break the content rules. The guest is told and the removal is recorded in the audit log. This cannot be undone.',
  admRemoving: 'Removing…',
  admConfirmRemoval: 'Confirm removal',
  admReviewRemoved: 'Review removed',

  // ------------------------------------------------------------- role labels
  roleUser: 'guest',
  roleManager: 'manager',
  roleAdmin: 'admin',
  roleSupervisor: 'supervisor',

  // Offline payments (money received at the venue, recorded by staff)
  payPaidFull: 'Paid in full',
  payReceived: '{paid} received',
  payBalanceDue: '{amount} balance due',
  payFullDue: '{amount} to pay at the venue',
  payRefunded: 'Refunded',
  payRecord: 'Record payment',
  payRecordTitle: 'Record a payment',
  payTotal: 'Booking total',
  payReceivedSoFar: 'Received so far',
  payBalance: 'Balance',
  payAmount: 'Amount received (₹)',
  payAmountRequired: 'Enter the amount received',
  payOverBalance: 'That is more than the balance of {amount}',
  payFillBalance: 'Full balance {amount}',
  payMethod: 'Paid by',
  payMethod_cash: 'Cash',
  payMethod_upi: 'UPI',
  payMethod_bank: 'Bank transfer',
  payMethod_card: 'Card (at the venue)',
  payMethod_other: 'Other',
  payReference: 'Reference',
  payReferenceHint: 'UPI / bank transaction ID, cheque no.…',
  paySave: 'Save payment',
  paySavedFull: 'Payment saved. Paid in full.',
  paySavedBalance: 'Payment saved. {amount} still due.',

  // Venue equipment shown to guests
  eqNone: 'The venue has not listed its equipment yet.',
  eqCount: '{name} × {count}',
  eqPartial: '{name}: {available} of {total} available ({count} {status})',
  eqOut: '{name} — {status}',
  eqNoticeTitle: 'Not fully available right now',
  eqNoticeAsk: 'If you need these for your event, ask the venue before you book.',
} satisfies Record<string, Phrase>;

/** The whole dictionary: the app's own strings plus the public site's areas. */
export const en = { ...base, ...coreEn, ...homeEn, ...exploreEn, ...venueEn, ...bookingEn, ...pagesEn, ...staffEn };
